import { Wordcloud } from '@buggy/shared'
import { useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { chartSize } from '@/utils/export-pptx/constants'

type UseWordCloudDataUri = {
  words: { text: string; value: number }[]
}

const useWordCloudDataUri = ({
  words,
}: UseWordCloudDataUri): string | undefined => {
  const [dataUri, setDataUri] = useState<string>()

  useEffect(() => {
    if (words.length === 0) {
      setDataUri('')
      return
    }

    const container = document.createElement('div')
    container.style.position = 'absolute'
    container.style.left = '-9999px'
    document.body.appendChild(container)

    const svgElement = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'svg',
    )
    svgElement.setAttribute('width', chartSize.wordCloud.w.toString())
    svgElement.setAttribute('height', chartSize.wordCloud.h.toString())
    container.appendChild(svgElement)

    const canvas = document.createElement('canvas')
    canvas.width = chartSize.wordCloud.w
    canvas.height = chartSize.wordCloud.h
    container.appendChild(canvas)

    const ctx = canvas.getContext('2d')
    if (!ctx) {
      return
    }

    const svgRefCallback = (el: SVGSVGElement | null): void => {
      if (!el) {
        return
      }

      svgElement.replaceWith(el)

      const observer = new MutationObserver(() => {
        if (el.children.length > 0) {
          observer.disconnect()

          const svgData = new XMLSerializer().serializeToString(el)
          const svgBlob = new Blob([svgData], {
            type: 'image/svg+xml;charset=utf-8',
          })
          const svgUrl = URL.createObjectURL(svgBlob)

          const img = new Image()
          img.addEventListener('load', () => {
            ctx.clearRect(0, 0, chartSize.wordCloud.w, chartSize.wordCloud.h)
            ctx.drawImage(
              img,
              0,
              0,
              chartSize.wordCloud.w,
              chartSize.wordCloud.h,
            )

            const pngDataUrl = canvas.toDataURL('image/png')
            setDataUri(pngDataUrl)

            document.body.removeChild(container)
            URL.revokeObjectURL(svgUrl)
          })
          img.src = svgUrl
        }
      })

      observer.observe(el, { childList: true, subtree: true })
    }

    const root = createRoot(container)
    root.render(
      <svg
        height={chartSize.wordCloud.h}
        ref={svgRefCallback}
        width={chartSize.wordCloud.w}
      >
        <Wordcloud
          height={chartSize.wordCloud.h}
          width={chartSize.wordCloud.w}
          words={words.slice(0, 100)}
        />
      </svg>,
    )
  }, [words])

  return dataUri
}

export default useWordCloudDataUri
